import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import API_CIFRA from '../../config/Variables_apis';

const CLAVE_TOKENS = 'cn_comentarios_tokens';

const leerTokens = () => {
  try { return JSON.parse(localStorage.getItem(CLAVE_TOKENS) || '{}'); } catch { return {}; }
};
const guardarToken = (id, token) => {
  const t = leerTokens();
  t[id] = token;
  localStorage.setItem(CLAVE_TOKENS, JSON.stringify(t));
};

/**
 * Comentarios de un punto/incidencia ya registrado. Muestra dos acciones:
 * "Comentar" (abre el formulario para registrar) y "Ver comentarios" (solo si
 * ya hay alguno). Si el autor guardó su token, puede eliminar su comentario
 * dentro de la ventana configurada. Visible en el modal del mapa y en el
 * seguimiento.
 */
const ComentariosPunto = ({ incidenciaId, configuracion }) => {
  const [comentarios, setComentarios] = useState([]);
  const [texto, setTexto] = useState('');
  const [alias, setAlias] = useState('');
  const [foto, setFoto] = useState(null);
  const [archivoPublico, setArchivoPublico] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [fotoAmpliada, setFotoAmpliada] = useState(null);
  const [vista, setVista] = useState(null); // null | 'form' | 'lista'
  const tokens = leerTokens();

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(API_CIFRA.incidencia.comentarios(incidenciaId));
      if (r.ok) setComentarios(await r.json());
    } catch { /* sin conexión */ }
  }, [incidenciaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const enviar = async (e) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    setMensaje('');
    try {
      const fd = new FormData();
      fd.append('texto', texto);
      if (alias) fd.append('alias', alias);
      if (foto) {
        fd.append('foto', foto);
        fd.append('archivo_publico', archivoPublico ? 'true' : 'false');
      }
      const r = await fetch(API_CIFRA.incidencia.comentarios(incidenciaId), { method: 'POST', body: fd });
      const data = await r.json();
      if (r.ok) {
        if (data.token) guardarToken(data.com_inId, data.token);
        setTexto(''); setAlias(''); setFoto(null); setArchivoPublico(true);
        setMensaje(data.mensaje || 'Comentario publicado.');
        cargar();
        setVista('lista');
      } else {
        setMensaje(data.error || 'No se pudo publicar el comentario.');
      }
    } catch {
      setMensaje('Sin conexión.');
    } finally {
      setEnviando(false);
    }
  };

  const eliminar = async (c) => {
    const token = tokens[c.com_inId];
    if (!token) return;
    try {
      const r = await fetch(`${API_CIFRA.incidencia.comentarioDetalle(c.com_inId)}?token=${token}`, { method: 'DELETE' });
      if (r.ok) cargar();
    } catch { /* ignore */ }
  };

  if (configuracion && configuracion.comentarios_activos === false) return null;

  return (
    <div className="cn-punto-comentarios">
      <div className="cn-punto-acciones">
        <button
          type="button"
          className={`cn-punto-btn${vista === 'form' ? ' cn-punto-btn-activo' : ''}`}
          onClick={() => setVista(vista === 'form' ? null : 'form')}
        >
          <i className="fas fa-comment"></i> Comentar
        </button>
        {comentarios.length > 0 && (
          <button
            type="button"
            className={`cn-punto-btn${vista === 'lista' ? ' cn-punto-btn-activo' : ''}`}
            onClick={() => setVista(vista === 'lista' ? null : 'lista')}
          >
            <i className="fas fa-comments"></i> Ver comentarios ({comentarios.length})
          </button>
        )}
      </div>

      {vista === 'form' && (
        <form className="cn-punto-form" onSubmit={enviar}>
          <input
            className="cn-punto-alias"
            placeholder="Tu nombre (opcional)"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
          />
          <textarea
            className="cn-punto-texto"
            rows={2}
            placeholder="Escribe un comentario…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
          <div className="cn-punto-fila">
            <input type="file" accept="image/*" onChange={(e) => setFoto(e.target.files?.[0] || null)} />
            <button type="submit" disabled={enviando || !texto.trim()}>
              {enviando ? 'Enviando…' : 'Publicar'}
            </button>
          </div>
          {foto && (
            <div className="cn-punto-visibilidad">
              <span>Visibilidad del archivo:</span>
              <label>
                <input
                  type="radio"
                  name={`cn-vis-${incidenciaId}`}
                  checked={archivoPublico}
                  onChange={() => setArchivoPublico(true)}
                />
                Público (lo ven todos)
              </label>
              <label>
                <input
                  type="radio"
                  name={`cn-vis-${incidenciaId}`}
                  checked={!archivoPublico}
                  onChange={() => setArchivoPublico(false)}
                />
                Privado (solo la entidad)
              </label>
            </div>
          )}
        </form>
      )}

      {vista === 'lista' && (
        <div className="cn-punto-lista">
          {comentarios.length === 0 && <p className="cn-punto-vacio">Todavía no hay comentarios.</p>}
          {comentarios.map((c) => (
            <div key={c.com_inId} className="cn-punto-comentario">
              <div className="cn-punto-comentario-cab">
                <strong>{c.com_chAlias || 'Vecino'}</strong>
                <span>
                  {new Date(c.com_dtFechaCreated).toLocaleString('es-PE', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                    timeZone: (configuracion && configuracion.zona_horaria) || 'America/Lima',
                  })}
                </span>
              </div>
              <p>{c.com_txTexto}</p>
              {c.com_imgFoto && (
                <img
                  src={c.com_imgFoto}
                  alt=""
                  className="cn-punto-comentario-foto"
                  role="button"
                  tabIndex={0}
                  onClick={() => setFotoAmpliada(c.com_imgFoto)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFotoAmpliada(c.com_imgFoto); }}
                />
              )}
              {tokens[c.com_inId] && (
                <button type="button" className="cn-punto-eliminar" onClick={() => eliminar(c)}>
                  Eliminar
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {vista === null && comentarios.length === 0 && (
        <p className="cn-punto-vacio">Sé el primero en comentar.</p>
      )}

      {mensaje && <p className="cn-punto-msg">{mensaje}</p>}

      {fotoAmpliada && createPortal(
        <div
          className="cn-foto-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setFotoAmpliada(null)}
        >
          <img
            src={fotoAmpliada}
            alt=""
            className="cn-foto-grande"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="cn-foto-cerrar"
            aria-label="Cerrar"
            onClick={() => setFotoAmpliada(null)}
          >
            ✕
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ComentariosPunto;
